import React, { useEffect, useState, useMemo } from "react";
import VRamFilter from "./VramFilter";
import {
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import { memoryToGiB, regionToReadable } from "components/utils";
import CloudFilter from "./CloudFilter";
import RegionFilter, {
  NO_REGION_FILTER,
  RegionFilterState,
  rawLocationsForZone,
  usePickerAllowedLocations,
} from "./RegionFilter";
import MoreFiltersPopover from "./MoreFiltersPopover";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import CapabilitiesFilter from "./CapabilitiesFilter";
import {
  getInstanceCloudCreds,
  getPrimaryInstanceCloudCred,
} from "utils/cloudCreds";
import ArchitectureFilter from "./ArchitectureFilter";
import { getInstanceTypeArchitectures } from "utils/architecture";
import { getFilteredBasePriceAmount } from "utils/freeTierUtils";

export const applyFilters = (
  instances: GPUInstanceType[],
  options: {
    vram?: string;
    gpuCount?: string;
    minRam?: string;
    minCPUCount?: number;
    priceRange?: number[];
    providers?: string[];
    architecture?: string;
    lockedProvider?: string | null;
    regionFilter?: RegionFilterState;
    persistentOnly?: boolean;
    flexiblePortControlOnly?: boolean;
    flexibleStorageOnly?: boolean;
    allowedLocations?: Set<string> | null;
  },
): GPUInstanceType[] => {
  let filteredInstances = instances;
  const hasGpu = (instance: GPUInstanceType) =>
    instance.supported_gpus && instance.supported_gpus.length > 0;

  // Locked provider filter (hard constraint — cloud credential ID, applied first)
  if (options.lockedProvider) {
    filteredInstances = filteredInstances.filter((instance) =>
      getInstanceCloudCreds(instance).some(
        (cloudCred) =>
          cloudCred.id?.toLowerCase() === options.lockedProvider!.toLowerCase(),
      ),
    );
  }

  // VRAM filter — CPU instances have no VRAM, so exclude them when this filter is active
  if (options.vram && options.vram !== "any") {
    const minMemoryGiB = memoryToGiB(options.vram);
    filteredInstances = filteredInstances.filter((instance) => {
      if (!hasGpu(instance)) {
        return false;
      }
      return memoryToGiB(instance.supported_gpus[0].memory) >= minMemoryGiB;
    });
  }

  // GPU count filter
  if (options.gpuCount && options.gpuCount !== "any") {
    filteredInstances = filteredInstances.filter((instance) => {
      if (!hasGpu(instance)) {
        return false;
      }
      if (options.gpuCount !== "8+") {
        return instance.supported_gpus[0].count === Number(options.gpuCount);
      } else {
        return instance.supported_gpus[0].count >= 8;
      }
    });
  }

  // RAM filter
  if (options.minRam && options.minRam !== "any") {
    filteredInstances = filteredInstances.filter(
      (instance) =>
        memoryToGiB(String(instance.memory)) >= memoryToGiB(options.minRam!),
    );
  }

  // CPU filter
  if (options.minCPUCount && options.minCPUCount > 0) {
    filteredInstances = filteredInstances.filter(
      (instance) => instance.vcpu >= options.minCPUCount!,
    );
  }

  // Price range filter
  if (
    options.priceRange &&
    (options.priceRange[0] !== 0 || options.priceRange[1] !== 100)
  ) {
    filteredInstances = filteredInstances.filter((instance) => {
      const price = getFilteredBasePriceAmount(
        instance,
        options.allowedLocations,
      );
      return (
        Number(price || 100) <= options.priceRange![1] &&
        Number(price || 0) >= options.priceRange![0]
      );
    });
  }

  // Provider filter
  if (options.providers?.length) {
    const selectedProviders = new Set(options.providers);
    filteredInstances = filteredInstances.filter((instance) => {
      const provider = GetProviderName(instance);
      return provider ? selectedProviders.has(provider) : false;
    });
  }

  // Architecture filter
  if (options.architecture && options.architecture !== "any") {
    filteredInstances = filteredInstances.filter((instance) =>
      getInstanceTypeArchitectures(instance).includes(options.architecture!),
    );
  }

  // Region filter (geo / country / zone)
  if (options.regionFilter) {
    const rf = options.regionFilter;
    if (rf.zone) {
      // Zone is a city name — resolve to all raw locations that map to it
      const zoneRawLocs = new Set(rawLocationsForZone(rf.zone));
      filteredInstances = filteredInstances.filter((instance) => {
        if (instance.location && zoneRawLocs.has(instance.location))
          return true;
        return (
          instance.available_locations?.some((l) => zoneRawLocs.has(l)) ?? false
        );
      });
    } else if (rf.country || rf.geo) {
      filteredInstances = filteredInstances.filter((instance) => {
        const locs = instance.available_locations?.length
          ? instance.available_locations
          : instance.location
            ? [instance.location]
            : [];
        return locs.some((loc) => {
          const info = regionToReadable[loc];
          if (!info) return false;
          if (rf.country && info.country !== rf.country) return false;
          if (rf.geo && info.geo !== rf.geo) return false;
          return true;
        });
      });
    }
  }

  // Filter out akash
  filteredInstances = filteredInstances.filter(
    (instance) =>
      getPrimaryInstanceCloudCred(instance)?.platformType !== "akash",
  );

  // Capability filters
  if (options.persistentOnly) {
    filteredInstances = filteredInstances.filter(
      (instance) => instance.stoppable === true,
    );
  }

  if (options.flexiblePortControlOnly) {
    filteredInstances = filteredInstances.filter(
      (instance) => instance.can_modify_firewall_rules === true,
    );
  }

  if (options.flexibleStorageOnly) {
    filteredInstances = filteredInstances.filter(
      (instance) => instance.elastic_root_volume === true,
    );
  }

  return filteredInstances;
};

interface FilterBarProps {
  allGPUInstances: GPUInstanceType[];
  allCPUInstances: GPUInstanceType[];
  setDisplayGPUInstances: (instances: GPUInstanceType[]) => void;
  setDisplayCPUInstances: (instances: GPUInstanceType[]) => void;
  containerSelected: ContainerSelected;
  onFilterStateChange: (
    persistentOnly: boolean,
    flexiblePortControlOnly: boolean,
    flexibleStorageOnly: boolean,
  ) => void;
  lockedProvider?: string | null;
  locationName?: string | null;
  regionFilter?: RegionFilterState;
  setRegionFilter?: (filter: RegionFilterState) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  allGPUInstances,
  allCPUInstances,
  setDisplayGPUInstances,
  setDisplayCPUInstances,
  containerSelected,
  onFilterStateChange,
  lockedProvider,
  locationName,
  regionFilter: controlledRegionFilter,
  setRegionFilter: setControlledRegionFilter,
}) => {
  const [selectedVRam, setSelectedVRam] = useState<string>("any");
  const [selectedGPUCount, setSelectedGPUCount] = useState<string>("any");
  const [selectMinRam, setSelectMinRam] = useState<string>("any");
  const [selectedMinCPUCount, setSelectedMinCPUCount] = useState<number>(0);
  const [priceRange, setPriceRange] = useState<number[]>([0, 100]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedArchitecture, setSelectedArchitecture] =
    useState<string>("any");
  const [internalRegionFilter, setInternalRegionFilter] =
    useState<RegionFilterState>(NO_REGION_FILTER);
  const regionFilter = controlledRegionFilter ?? internalRegionFilter;
  const setRegionFilter = setControlledRegionFilter ?? setInternalRegionFilter;
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);
  const [flexibleStorageOnly, setFlexibleStorageOnly] =
    useState<boolean>(false);

  const pickerAllowedLocations = usePickerAllowedLocations(
    regionFilter,
    locationName,
  );

  // Extract filtering logic into a reusable function

  // Calculate filtered instances before capability filters for showing counts in CapabilitiesFilter
  const filteredInstancesBeforeCapabilities = useMemo(() => {
    if (!allGPUInstances) return undefined;

    return applyFilters(allGPUInstances, {
      vram: selectedVRam,
      gpuCount: selectedGPUCount,
      minRam: selectMinRam,
      minCPUCount: selectedMinCPUCount,
      priceRange: priceRange,
      providers: selectedProviders,
      architecture: selectedArchitecture,
      lockedProvider,
      regionFilter,
      allowedLocations: pickerAllowedLocations,
    });
  }, [
    allGPUInstances,
    selectedVRam,
    selectedGPUCount,
    selectMinRam,
    selectedMinCPUCount,
    priceRange,
    selectedProviders,
    selectedArchitecture,
    regionFilter,
    pickerAllowedLocations,
  ]);

  useEffect(() => {
    if (!allGPUInstances || !allCPUInstances) return;

    const filterOptions = {
      vram: selectedVRam,
      gpuCount: selectedGPUCount,
      minRam: selectMinRam,
      minCPUCount: selectedMinCPUCount,
      priceRange: priceRange,
      providers: selectedProviders,
      architecture: selectedArchitecture,
      lockedProvider,
      regionFilter,
      persistentOnly,
      flexiblePortControlOnly,
      flexibleStorageOnly,
      allowedLocations: pickerAllowedLocations,
    };

    // Apply all filters including capabilities
    const filteredGPUInstances = applyFilters(allGPUInstances, filterOptions);
    const filteredCPUInstances = applyFilters(allCPUInstances, filterOptions);

    setDisplayGPUInstances(filteredGPUInstances);
    setDisplayCPUInstances(filteredCPUInstances);
  }, [
    allGPUInstances,
    allCPUInstances,
    selectedVRam,
    selectedGPUCount,
    selectMinRam,
    selectedMinCPUCount,
    priceRange,
    selectedProviders,
    selectedArchitecture,
    regionFilter,
    persistentOnly,
    flexiblePortControlOnly,
    flexibleStorageOnly,
    pickerAllowedLocations,
    setDisplayCPUInstances,
  ]);

  // Notify parent of filter state changes
  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange(
        persistentOnly,
        flexiblePortControlOnly,
        flexibleStorageOnly,
      );
    }
  }, [
    persistentOnly,
    flexiblePortControlOnly,
    flexibleStorageOnly,
    onFilterStateChange,
  ]);

  return (
    <div className="overflow-auto flex flex-row w-full gap-2 items-start lg:flex-row flex-wrap">
      <div className="flex lg:flex-1">
        <div className="">
          <CapabilitiesFilter
            persistentOnly={persistentOnly}
            setPersistentOnly={setPersistentOnly}
            flexiblePortControlOnly={flexiblePortControlOnly}
            setFlexiblePortControlOnly={setFlexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            setFlexibleStorageOnly={setFlexibleStorageOnly}
            allGPUInstances={filteredInstancesBeforeCapabilities}
          />
        </div>
      </div>
      <VRamFilter
        selectedVRam={selectedVRam}
        setSelectedVRam={setSelectedVRam}
      />
      <ArchitectureFilter
        selectedArchitecture={selectedArchitecture}
        setSelectedArchitecture={setSelectedArchitecture}
        allInstances={[...allGPUInstances, ...allCPUInstances]}
      />
      <CloudFilter
        selectedProviders={selectedProviders}
        setSelectedProviders={setSelectedProviders}
        containerSelected={containerSelected}
        allGPUInstances={[...allGPUInstances, ...allCPUInstances]}
        lockedProvider={lockedProvider}
      />
      <RegionFilter
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        allInstances={[...allGPUInstances, ...allCPUInstances]}
      />
      <MoreFiltersPopover
        selectedGPUCount={selectedGPUCount}
        setSelectedGPUCount={setSelectedGPUCount}
        selectedMinCPUCount={selectedMinCPUCount}
        setSelectedMinCPUCount={setSelectedMinCPUCount}
        selectMinRam={selectMinRam}
        setSelectMinRam={setSelectMinRam}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
      />
    </div>
  );
};

export default FilterBar;
